/**
 * Authentication Domain Model
 *
 * Defines the complete type system for the authentication state machine.
 * Inspired by NextAuth / Auth0 SDK / Supabase Auth patterns.
 *
 * Key design decisions:
 * - Discriminated union on `status` for exhaustive pattern matching
 * - No magic booleans — every state is explicit
 * - AuthSession is the single source of truth for token + user context
 */

// ---------------------------------------------------------------------------
// Auth Status — explicit state machine states
// ---------------------------------------------------------------------------

const AUTH_STATUS = {
  /** App just mounted; auth has not started yet */
  UNINITIALIZED: "UNINITIALIZED",
  /** Checking for a persisted session in storage */
  RESTORING_SESSION: "RESTORING_SESSION",
  /** Exchanging an OAuth authorization code for a token */
  AUTHENTICATING: "AUTHENTICATING",
  /** Successfully authenticated — session is available */
  AUTHENTICATED: "AUTHENTICATED",
  /** No valid session could be established */
  UNAUTHENTICATED: "UNAUTHENTICATED",
  /** An unrecoverable error occurred during authentication */
  ERROR: "ERROR",
} as const

type AuthStatus = (typeof AUTH_STATUS)[keyof typeof AUTH_STATUS]

// ---------------------------------------------------------------------------
// Auth Token
// ---------------------------------------------------------------------------

interface AuthToken {
  accessToken: string
  deviceId: string
}

// ---------------------------------------------------------------------------
// Auth Session — the "logged-in" payload
// ---------------------------------------------------------------------------

interface AuthSession {
  token: AuthToken
  partnerIdentifier: string
}

// ---------------------------------------------------------------------------
// Auth Error — structured error for the ERROR state
// ---------------------------------------------------------------------------

interface AuthError {
  message: string
  code?: string
  /** Original error for logging — never exposed to UI */
  cause?: unknown
}

// ---------------------------------------------------------------------------
// Auth State — discriminated union (single source of truth)
// ---------------------------------------------------------------------------

interface AuthStateUninitialized {
  status: typeof AUTH_STATUS.UNINITIALIZED
  session: null
  error: null
}

interface AuthStateRestoringSession {
  status: typeof AUTH_STATUS.RESTORING_SESSION
  session: null
  error: null
}

interface AuthStateAuthenticating {
  status: typeof AUTH_STATUS.AUTHENTICATING
  session: null
  error: null
}

interface AuthStateAuthenticated {
  status: typeof AUTH_STATUS.AUTHENTICATED
  session: AuthSession
  error: null
}

interface AuthStateUnauthenticated {
  status: typeof AUTH_STATUS.UNAUTHENTICATED
  session: null
  error: null
}

interface AuthStateError {
  status: typeof AUTH_STATUS.ERROR
  session: null
  error: AuthError
}

type AuthState =
  | AuthStateUninitialized
  | AuthStateRestoringSession
  | AuthStateAuthenticating
  | AuthStateAuthenticated
  | AuthStateUnauthenticated
  | AuthStateError

// ---------------------------------------------------------------------------
// OAuth Params — parsed from URL
// ---------------------------------------------------------------------------

interface OAuthParams {
  code: string
  codeVerifier?: string
}

interface InternalTestingParams {
  partnerId: number
  partnerSecret: string
  phoneNo: string
  fullname: string
}

interface ProviderErrorParams {
  error: string
  description?: string
}

type AuthenticationParams =
  | { kind: "oauth"; params: OAuthParams }
  | { kind: "persisted" }
  | { kind: "internal-testing"; params: InternalTestingParams }
  | { kind: "provider-error"; params: ProviderErrorParams }
  | { kind: "none" }

// ---------------------------------------------------------------------------
// Auth Context — what consumers receive from useAuth()
// ---------------------------------------------------------------------------

interface AuthContextValue {
  /** Current auth state (discriminated union) */
  state: AuthState
  /** Convenience: true only when status === AUTHENTICATED */
  isAuthenticated: boolean
  /** Convenience: true while status is RESTORING_SESSION or AUTHENTICATING */
  isLoading: boolean
  /** The active session, or null */
  session: AuthSession | null
  /** Trigger a full logout and state reset */
  logout: () => void
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { AUTH_STATUS }
export type {
  AuthStatus,
  AuthToken,
  AuthSession,
  AuthError,
  AuthState,
  AuthStateUninitialized,
  AuthStateRestoringSession,
  AuthStateAuthenticating,
  AuthStateAuthenticated,
  AuthStateUnauthenticated,
  AuthStateError,
  OAuthParams,
  InternalTestingParams,
  ProviderErrorParams,
  AuthenticationParams,
  AuthContextValue,
}
